import {Alert, Checkbox, Col, Row, Image, Space, Tag, Select, Divider} from 'antd'
import React, {useState} from 'react'
import {useIntl} from 'react-intl'
import {PlantCollection, PlantWithProjectName} from '../../entities/plant'
import {useAsyncValue} from '../../hooks/useAsyncValue'
import {instance as projectsService} from '../../services/ProjectsService'
import {ImagePreview} from './components/ImagePreview'
import {PlantCalendar} from './components/PlantCalendar'
import {Panel} from './components/Panel'
import {FontAwesomeIcon as Icon} from '@fortawesome/react-fontawesome'
import * as Icons from '@fortawesome/free-solid-svg-icons'
import {useRevalidator, useSubmit} from 'react-router-dom'
import {ImageTagView} from './ImageTagView'
import {PlantCollectionSelector} from './components/PlantCollectionSelector'
import {instance as plantsService} from '../../services/PlantsService'
import {instance as elementTypeService} from '../../services/ElementTypeService'
import {instance as gardenPlanVariantsService} from '../../services/GardenPlanVariantsService'
import Markdown from 'react-markdown'
import {PlantTemplateSelector} from './components/PlantTemplateSelector'
import {ProjectFormDropdown} from './components/ProjectFormDropdown'
import {markProjectForRevalidation} from '../../routes/projectRevalidator'
import {markPlantForRevalidation} from '../../routes/plantRevalidator'
import {MultiValueTag} from './components/MultiValueTag'

const normalizeFile = (e: any) => {
  if (Array.isArray(e)) {
    return e
  }
  return e?.fileList
}

export type PlantFormViewContentProps = {
  currentPlant: PlantWithProjectName
  plantTitlePhoto: string
  projectsList: {id: number; name: string}[]
  userPlantCollections: PlantCollection[]
}

export const PlantFormViewContent = ({
  currentPlant,
  plantTitlePhoto,
  projectsList,
  userPlantCollections,
}: PlantFormViewContentProps) => {
  const intl = useIntl()
  //const projectElements = projectsService.getUserProjectElements(currentPlant.projectId)

  const currentPlantFields = currentPlant.fields
  const revalidator = useRevalidator()

  const [timelineCollapsed, setTimelineCollapsed] = useState(false)
  const [confidenceInPlantVariety, setConfidenceInPlantVariety] = useState(
    currentPlant?.confidenceInPlantVariety
  )

  const submit = useSubmit()
  const refreshPlantData = () => {
    submit(null, {method: 'post'})
    markPlantForRevalidation()
  }

  const {
    value: projectElement,
    isLoading: projectElementLoading,
    error,
  } = useAsyncValue(
    '',
    async () => {
      if (!currentPlant.projectId) {
        return undefined
      }

      const projectElements = await projectsService.getUserProjectElements(currentPlant.projectId)
      return projectElements.find((x) => x.elementId === currentPlant.elementId).name
    },
    [currentPlant]
  )

  const {value: gardenPlanVariantName} = useAsyncValue(
    undefined as string | undefined,
    async () => {
      if (!currentPlant.gardenPlanVariantId || !currentPlant.projectId) {
        return undefined
      }

      const result = await gardenPlanVariantsService.getProjectGardenPlanVariants(
        currentPlant.projectId
      )
      return result.success
        ? result.projectGardenPlanVariants.find(
            (variant) => variant.id === currentPlant.gardenPlanVariantId
          )?.name
        : undefined
    },
    [currentPlant.gardenPlanVariantId, currentPlant.projectId]
  )

  // The tags sit in a panel of their own, so it only earns its background and
  // its spacing once at least one of them has something to say.
  const hasTags = Boolean(
    currentPlantFields['Тип']?.length ||
      currentPlantFields['Полив'] ||
      currentPlantFields['Отношение к освещенности']?.length ||
      currentPlantFields['Сложность ухода'] ||
      currentPlantFields['Зоны морозостойкости (USDA-зоны)']?.length
  )

  return (
    <div className="main-column__left">
      <Row>
        <Col className="main-column__photo">
          <Image src={plantTitlePhoto} preview={false}></Image>
        </Col>
        <Col className="main-column__names">
          <div className="main-column__names__title">
            {currentPlantFields['Общераспространенное название']} {currentPlantFields.Сорт}{' '}
            {currentPlantFields['Уточняющая характеристика']
              ? `(${currentPlantFields['Уточняющая характеристика']})`
              : ''}
          </div>
          <div className="main-column__names__latin">
            {currentPlantFields['Научное (латинское) название']}
          </div>
          {currentPlantFields['Другие названия'] && (
            <div>
              {intl.formatMessage({id: 'otherNames'})}:{' '}
              {currentPlantFields['Другие названия'].replace('\n', ', ')}
            </div>
          )}
          <div className="main-column__names__family">{currentPlantFields['Семейство']}</div>

          <Space className="main-column__factors-tags" size={[0, 8]} wrap>
            {currentPlantFields['Факторы привлекательности'] &&
              currentPlantFields['Факторы привлекательности'].map((f, index) => (
                <Tag key={index} color="gold">
                  {f}
                </Tag>
              ))}
          </Space>
        </Col>
      </Row>
      <Divider orientation="horizontal"></Divider>
      <Row>
        <div className="main-column__user">
          {/* The selectors read as one form: every caption sits above a field that
              spans the whole column, so they line up and share a height. */}
          <div className="main-column__user__fields">
            {currentPlant.projectId && (
              <div className="main-column__user__fields__field">
                <span className="main-column__user__fields__label">
                  {intl.formatMessage({id: 'thePlantIsInTheProject'})}:
                </span>
                <Select
                  className="main-column__user__fields__control"
                  size="large"
                  allowClear
                  disabled={!!currentPlant.gardenPlanVariantId}
                  onChange={async (projectId) => {
                    await plantsService.editPlantProjectByPlantId(currentPlant.id, projectId)
                    markProjectForRevalidation()
                    refreshPlantData()
                    revalidator.revalidate()
                  }}
                  defaultValue={currentPlant.projectId}
                  options={projectsList.map((project) => ({
                    label: project.name,
                    value: project.id,
                  }))}
                ></Select>
                {gardenPlanVariantName && (
                  <Alert
                    type="warning"
                    showIcon
                    title={intl.formatMessage(
                      {id: 'plantIsCreatedInGardenPlan'},
                      {planName: gardenPlanVariantName}
                    )}
                  />
                )}
              </div>
            )}

            {currentPlant.elementId && (
              <div className="main-column__user__fields__field main-column__user__fields__field--text">
                <span className="main-column__user__fields__label">
                  {intl.formatMessage({id: 'onElement'})}:
                </span>
                <span className="main-column__user__input">{projectElement}</span>
              </div>
            )}
            {/* {currentPlant.dateOfPlantingUtc && (
              <span style={{paddingTop: 10, paddingBottom: 10}}>
                {intl.formatMessage({id: 'dateOfPlanting'})}:
                <span className="main-column__user__input">
                  {` ${currentPlant.dateOfPlantingUtc}`}
                </span>
              </span>
            )} */}

            <div className="main-column__user__fields__field">
              <span className="main-column__user__fields__label">
                {intl.formatMessage({id: 'plantDesignationOnTheMap'})}:
              </span>
              <PlantTemplateSelector
                className="main-column__user__fields__control"
                userPlantTemplateId={elementTypeService.chooseElementTypeTemplate(
                  'plant',
                  currentPlant.template
                )}
                plantId={currentPlant ? currentPlant.id : null}
              />
            </div>

            <div className="main-column__user__fields__field">
              <span className="main-column__user__fields__label">
                {intl.formatMessage({id: 'myCollections'})}:
              </span>
              <PlantCollectionSelector
                className="main-column__user__fields__control"
                plantCollections={currentPlant.collections}
                plantId={currentPlant ? currentPlant.id : null}
                onPlantCollectionsListUpdated={refreshPlantData}
                userPlantCollections={userPlantCollections}
              />
            </div>
          </div>
          <Checkbox
            checked={confidenceInPlantVariety}
            disabled={currentPlant ? false : true}
            onChange={async (e) => {
              plantsService.editConfidenceInPlantVarietyByPlantId(currentPlant.id, e.target.checked)
              setConfidenceInPlantVariety(e.target.checked)
            }}
          >
            {intl.formatMessage({id: 'confidenceInPlantVariety'})}
          </Checkbox>

          {currentPlant.comment && (
            <div>
              {intl.formatMessage({id: 'comment'})}:
              <span className="main-column__user__input">{` ${currentPlant.comment}`}</span>
            </div>
          )}

          {hasTags && <Divider orientation="horizontal"></Divider>}
          {currentPlant?.photos?.length > 0 && (
            <div>
              {intl.formatMessage({id: 'myPhotos'})}:
              <ImagePreview photosIds={currentPlant.photos} />
            </div>
          )}
          {currentPlant.plantBaseId &&
            (currentPlantFields?.['Фотографии растения']?.length ? 1 : 0) +
              (currentPlantFields?.['Фотографии цветов']?.length ? 1 : 0) +
              (currentPlantFields?.['Фотографии листьев']?.length ? 1 : 0) +
              (currentPlantFields?.['Фотографии плодов']?.length ? 1 : 0) >
              0 && (
              <div>
                {/* {intl.formatMessage({id: 'photos'})}: */}
                <ImageTagView
                  plantName={currentPlant.name}
                  basePlantPhotos={currentPlantFields?.['Фотографии растения']}
                  basePlantFlowerPhotos={currentPlantFields?.['Фотографии цветов']}
                  basePlantLeavesPhotos={currentPlantFields?.['Фотографии листьев']}
                  basePlantFruitsPhotos={currentPlantFields?.['Фотографии плодов']}
                  plantBaseId={currentPlant.plantBaseId}
                />
              </div>
            )}
        </div>
      </Row>
      {hasTags && (
        <div className="main-column__tags">
          {/* The row is the width the tags have to fit into, so it is also what
              the multi-value ones measure themselves against. */}
          <div className="main-column__tags__row">
            {currentPlantFields['Тип'] &&
              currentPlantFields['Тип'].map((type, index) => <Tag key={index}>{type}</Tag>)}
            {currentPlantFields['Полив'] && (
              <Tag icon={<Icon icon={Icons.faDroplet} />} color="blue">
                {currentPlantFields['Полив']}
              </Tag>
            )}
            {currentPlantFields['Отношение к освещенности'] && (
              <MultiValueTag
                values={currentPlantFields['Отношение к освещенности']}
                icon={<Icon icon={Icons.faSun} />}
                color="yellow"
              />
            )}
            {currentPlantFields['Сложность ухода'] && (
              <Tag icon={<Icon icon={Icons.faHandHoldingHand} />} color="purple">
                {currentPlantFields['Сложность ухода']} {intl.formatMessage({id: 'careDifficulty'})}
              </Tag>
            )}
            {currentPlantFields['Зоны морозостойкости (USDA-зоны)'] && (
              <MultiValueTag
                values={currentPlantFields['Зоны морозостойкости (USDA-зоны)']}
                icon={<Icon icon={Icons.faSnowflake} />}
                color="deepskyblue"
                suffix={` (${intl.formatMessage({id: 'uSDA'})})`}
              />
            )}
          </div>
        </div>
      )}
      {(currentPlantFields['Описание сорта'] ||
        currentPlantFields['Достоинства сорта'] ||
        currentPlantFields['Недостатки сорта']) && (
        <Panel title="description">
          {currentPlantFields['Описание сорта'] && (
            <div>
              <div className="main-column__base-info">
                <Markdown skipHtml={true}>{currentPlantFields['Описание сорта']}</Markdown>
              </div>
            </div>
          )}
          {currentPlantFields['Достоинства сорта'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'advantages'})}:
              </span>
              <div className="main-column__base-info">
                {currentPlantFields['Достоинства сорта']}
              </div>
            </div>
          )}
          {currentPlantFields['Недостатки сорта'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'disadvantages'})}:
              </span>
              <div className="main-column__base-info">{currentPlantFields['Недостатки сорта']}</div>
            </div>
          )}
        </Panel>
      )}
      {(currentPlantFields['Жизненный цикл'] ||
        currentPlantFields['Время плодоношения'] ||
        currentPlantFields['Время посадки на рассаду в закрытый грунт'] ||
        currentPlantFields['Время посадки на рассаду в открытый грунт'] ||
        currentPlantFields['Время цветения'] ||
        currentPlantFields['Время обрезки']) && (
        <Panel title="lifeCycle">
          <div className="main-column__calendar">
            {currentPlantFields['Жизненный цикл'] && (
              <Tag color="#f58153">{currentPlantFields['Жизненный цикл']?.join(', ')}</Tag>
            )}
            {currentPlantFields['Время посадки на рассаду в открытый грунт']?.includes(
              'после минования угрозы заморозков'
            ) && (
              <Tag color="#f58153">
                {intl.formatMessage({
                  id: 'timeForPlantingSeedlingsInOpenedGround-после минования угрозы заморозков',
                })}
              </Tag>
            )}
            {(currentPlantFields['Время плодоношения'] ||
              currentPlantFields['Время посадки на рассаду в закрытый грунт'] ||
              currentPlantFields['Время посадки на рассаду в открытый грунт'] ||
              currentPlantFields['Время цветения'] ||
              currentPlantFields['Время обрезки']) && (
              <PlantCalendar
                lifeCycle={currentPlantFields['Жизненный цикл']}
                fruitingTime={currentPlantFields['Время плодоношения']}
                plantingCloseTime={currentPlantFields['Время посадки на рассаду в закрытый грунт']}
                plantingOpenTime={currentPlantFields['Время посадки на рассаду в открытый грунт']}
                bloomingTime={currentPlantFields['Время цветения']}
                pruningTime={currentPlantFields['Время обрезки']}
                mode={timelineCollapsed ? 'oneLine' : 'multiLine'}
              />
            )}
          </div>
        </Panel>
      )}
      {(currentPlantFields['Высота от (м)'] ||
        currentPlantFields['Высота до (м)'] ||
        currentPlantFields['Размер цветов (см)'] ||
        currentPlantFields['Раскидистость от (м)'] ||
        currentPlantFields['Раскидистость до (м)'] ||
        currentPlantFields['Масса плодов (г)'] ||
        currentPlantFields['Цвет листьев'] ||
        currentPlantFields['Цвет цветов']) && (
        <Panel title="appearance">
          {(currentPlantFields['Высота от (м)'] || currentPlantFields['Высота до (м)']) && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'height'})}
                {': '}
              </span>
              {currentPlantFields['Высота от (м)'] ? currentPlantFields['Высота от (м)'] : '...'}{' '}
              {' - '}{' '}
              {currentPlantFields['Высота до (м)'] ? currentPlantFields['Высота до (м)'] : '...'}{' '}
              {intl.formatMessage({id: 'm'})}
            </div>
          )}
          {(currentPlantFields['Раскидистость от (м)'] ||
            currentPlantFields['Раскидистость до (м)']) && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'spreading'})}
                {': '}
              </span>
              {currentPlantFields['Раскидистость от (м)']
                ? currentPlantFields['Раскидистость от (м)']
                : '...'}{' '}
              {' - '}{' '}
              {currentPlantFields['Раскидистость до (м)']
                ? currentPlantFields['Раскидистость до (м)']
                : '...'}{' '}
              {intl.formatMessage({id: 'm'})}
            </div>
          )}
          {currentPlantFields['Размер цветов (см)'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'flowerSize'})}
                {': '}
              </span>
              {currentPlantFields['Размер цветов (см)']} {intl.formatMessage({id: 'cm'})}
            </div>
          )}
          {currentPlantFields['Масса плодов (г)'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'fruitMass'})}
                {': '}
              </span>
              {currentPlantFields['Масса плодов (г)']} {intl.formatMessage({id: 'g'})}
            </div>
          )}
          {currentPlantFields['Цвет листьев'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'leafColor'})}
                {': '}
              </span>
              {currentPlantFields['Цвет листьев']}
            </div>
          )}
          {currentPlantFields['Цвет цветов'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'flowerColor'})}
                {': '}
              </span>
              {currentPlantFields['Цвет цветов']}
            </div>
          )}
        </Panel>
      )}
      {(currentPlantFields['Глубина посадки (см)'] ||
        currentPlantFields['Особенности хранения посадочного материала'] ||
        currentPlantFields['Способы размножения'] ||
        currentPlantFields['Особенности выращивания'] ||
        currentPlantFields['Почва (субстрат)']) && (
        <Panel title="plantingAndReproduction">
          <div className="main-column__plant">
            {currentPlantFields['Глубина посадки (см)'] && (
              <div>
                <span className="plant-title-info-names">
                  {intl.formatMessage({id: 'plantingDepth'})}
                  {': '}
                </span>
                {currentPlantFields['Глубина посадки (см)']}
              </div>
            )}
            {currentPlantFields['Особенности хранения посадочного материала'] && (
              <div>
                <span className="plant-title-info-names">
                  {intl.formatMessage({id: 'featuresOfStoringPlantingMaterial'})}
                  {': '}
                </span>
                {currentPlantFields['Особенности хранения посадочного материала']}
              </div>
            )}
            {currentPlantFields['Способы размножения'] && (
              <div>
                <span className="plant-title-info-names">
                  {intl.formatMessage({id: 'reproductionMethods'})}
                  {': '}
                </span>
                {currentPlantFields['Способы размножения'].join(', ')}
              </div>
            )}
            {currentPlantFields['Особенности выращивания'] && (
              <div>
                <span className="plant-title-info-names">
                  {intl.formatMessage({id: 'featuresOfCultivation'})}
                  {': '}
                </span>
                {currentPlantFields['Особенности выращивания'].join(', ')}
              </div>
            )}
            {currentPlantFields['Почва (субстрат)'] && (
              <div>
                <span className="plant-title-info-names">
                  {intl.formatMessage({id: 'soil'})}
                  {': '}
                </span>
                {currentPlantFields['Почва (субстрат)']}
              </div>
            )}
          </div>
        </Panel>
      )}
      {(currentPlantFields['Дополнительные действия по уходу'] ||
        currentPlantFields['Болезни'] ||
        currentPlantFields['Вредители'] ||
        currentPlantFields['Пестициды'] ||
        currentPlantFields['Удобрения'] ||
        currentPlantFields['Частота внесения удобрений'] ||
        currentPlantFields['Частота обработки пестицидами'] ||
        currentPlantFields['Особенности хранения плодов'] ||
        currentPlantFields['Ядовитость']) && (
        <Panel title="care">
          {currentPlantFields['Дополнительные действия по уходу'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'additionalCareSteps'})}
                {': '}
              </span>
              {currentPlantFields['Дополнительные действия по уходу'].join(', ')}
            </div>
          )}
          {currentPlantFields['Болезни'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'disease'})}
                {': '}
              </span>
              {currentPlantFields['Болезни'].join(', ')}
            </div>
          )}
          {currentPlantFields['Вредители'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'pests'})}
                {': '}
              </span>
              {currentPlantFields['Вредители'].join(', ')}
            </div>
          )}
          {currentPlantFields['Пестициды'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'pesticides'})}
                {': '}
              </span>
              {currentPlantFields['Пестициды']}
            </div>
          )}
          {currentPlantFields['Удобрения'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'fertilizers'})}
                {': '}
              </span>
              {currentPlantFields['Удобрения']}
            </div>
          )}
          {currentPlantFields['Частота внесения удобрений'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'fertilizerFrequency'})}
                {': '}
              </span>
              {currentPlantFields['Частота внесения удобрений']}
            </div>
          )}
          {currentPlantFields['Частота обработки пестицидами'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'pesticideTreatmentFrequency'})}
                {': '}
              </span>
              {currentPlantFields['Частота обработки пестицидами'].join(', ')}
            </div>
          )}
          {currentPlantFields['Особенности хранения плодов'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'featuresOfFruitStorage'})}
                {': '}
              </span>
              {currentPlantFields['Особенности хранения плодов']}
            </div>
          )}
          {currentPlantFields['Ядовитость'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'toxicity'})}
                {': '}
              </span>
              {currentPlantFields['Ядовитость']}
            </div>
          )}
        </Panel>
      )}
      {(currentPlantFields['Применение в дизайне'] ||
        currentPlantFields['Назначение/местоположение'] ||
        currentPlantFields['Съедобность'] ||
        currentPlantFields['Медицинские свойства']) && (
        <Panel title="usage">
          {currentPlantFields['Применение в дизайне'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'applicationInDesign'})}
                {': '}
              </span>
              {currentPlantFields['Применение в дизайне'].join(', ')}
            </div>
          )}
          {/* {currentPlantFields['факторы'] && (
              <div>
                {intl.formatMessage({id: 'fertilizerFrequency'})} {': '}
                {currentPlantFields['факторы'].join(', ')}
              </div>
            )} */}
          {currentPlantFields['Назначение/местоположение'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'purposeLocation'})}
                {': '}
              </span>
              {currentPlantFields['Назначение/местоположение'].join(', ')}
            </div>
          )}
          {currentPlantFields['Съедобность'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'edibility'})}
                {': '}
              </span>
              {currentPlantFields['Съедобность']}
            </div>
          )}
          {currentPlantFields['Медицинские свойства'] && (
            <div>
              <span className="plant-title-info-names">
                {intl.formatMessage({id: 'medicalProperties'})}
                {': '}
              </span>
              {currentPlantFields['Медицинские свойства']}
            </div>
          )}
        </Panel>
      )}
    </div>
  )
}
